import { useEffect, useRef, useState } from 'react';
import { BordLateralModal } from 'bord-design-system';
import {
  IlogisticServiceDetails,
  IlogisticServiceDetailsToolsData,
  IlogisticServiceTrelloCardData
} from '../../../../../types/services';
import useDeliveredToolsState, { SECTIONS_STEPS } from 'state/useDeliveredToolsState';
import SelectedToolsSection from './components/sections/SelectedToolsSection';
import ConfirmationSection from './components/sections/ConfirmationSection';
import AddAdditionalToolsSection from './components/sections/AddAdditionalToolsSection';
import type { OffboardingLateralChrome } from './offboardingLateralModal.types';

interface OffboardingToWarehouseModalProps {
  cardData: IlogisticServiceTrelloCardData;
  serviceDetails: IlogisticServiceDetails;
  closeModalCallback: () => void;
  closeModalAfterSubmitCallback: () => void;
  isUpdateTools?: boolean;
}

/**
 * Modal de offboarding a bodega con panel lateral (`BordLateralModal`).
 */
export const OffboardingToWarehouseModal = (props: OffboardingToWarehouseModalProps) => {
  return <OffboardingToWarehouseBordLateralModal {...props} />;
};

/**
 * Variante con panel lateral y cabecera/pie definidos por los pasos de selección y herramientas adicionales.
 */
const OffboardingToWarehouseBordLateralModal = ({
  closeModalCallback,
  closeModalAfterSubmitCallback,
  cardData,
  serviceDetails,
  isUpdateTools = false
}: OffboardingToWarehouseModalProps) => {
  const {
    currentStep,
    setAvailableTools,
    setSelectedTools,
    setUnselectedTools,
    setMarkedNotDeliveredToolIds,
    resetState
  } = useDeliveredToolsState();
  const hasInitializedRef = useRef<boolean>(false);
  const [lateralChrome, setLateralChrome] = useState<OffboardingLateralChrome | null>(null);

  /**
   * Inicializa los estados de las herramientas disponibles y organiza las herramientas
   * seleccionadas y no seleccionadas según su estado de entrega cuando es una actualización.
   * @param tools - Lista de herramientas del servicio
   * @param isUpdate - Indica si es una actualización de herramientas
   */
  const initializeToolsState = (tools?: IlogisticServiceDetailsToolsData[] | null, isUpdate?: boolean) => {
    if (!tools || hasInitializedRef.current) return;

    if (isUpdate) {
      setAvailableTools(tools);

      const selectedIndices: number[] = [];
      const unselectedIndices: number[] = [];

      tools.forEach((tool, index) => {
        if (tool.isMarkedNotDelivered === true) {
          unselectedIndices.push(index);
        } else {
          selectedIndices.push(index);
        }
      });

      setSelectedTools(selectedIndices);
      setUnselectedTools(unselectedIndices);
    } else {
      const deliverableTools = tools.filter(tool => tool?.isMarkedNotDelivered !== true);
      const notDeliveredToolIds = tools
        .filter(tool => tool?.isMarkedNotDelivered === true && Number.isInteger(tool?.productId))
        .map(tool => tool.productId as number);
      setAvailableTools(deliverableTools);
      setMarkedNotDeliveredToolIds(notDeliveredToolIds);
    }

    hasInitializedRef.current = true;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case SECTIONS_STEPS.SELECTION:
        return (
          <SelectedToolsSection
            closeModalCallback={closeModalCallback}
            cardData={cardData}
            isUpdateTools={isUpdateTools}
            onLateralChromeChange={setLateralChrome}
          />
        );
      case SECTIONS_STEPS.ADD_ADDITIONAL_TOOLS:
        return (
          <AddAdditionalToolsSection
            closeModalCallback={closeModalCallback}
            cardData={cardData}
            serviceDetails={serviceDetails}
            isUpdateTools={isUpdateTools}
            onLateralChromeChange={setLateralChrome}
          />
        );
      case SECTIONS_STEPS.CONFIRMATION:
        return (
          <ConfirmationSection
            closeModalCallback={closeModalCallback}
            cardData={cardData}
            serviceDetails={serviceDetails}
            closeModalAfterSubmitCallback={closeModalAfterSubmitCallback}
            isUpdateTools={isUpdateTools}
            onLateralChromeChange={setLateralChrome}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    initializeToolsState(serviceDetails?.tools, isUpdateTools);
    // Inicialización acotada a tools / modo actualización; la función usa ref interno.
  }, [serviceDetails?.tools, isUpdateTools]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => resetState();
  }, [resetState]);

  return (
    <BordLateralModal
      open
      onClose={closeModalCallback}
      closesOnOverlayClick
      bordLateralModalFooterClassName="bordLateralModalFooterDScenteredFullWidth"
      bordLateralModalHeaderProps={lateralChrome?.headerProps}
      bordLateralModalContent={renderStepContent()}
      bordLateralModalFooterProps={lateralChrome?.footerProps}
      panelMaxWidth='56rem'
    />
  );
};
